import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, GraduationCap, Search, ArrowUpDown, TrendingUp, TrendingDown, Minus, FileDown, Loader2, CheckCircle2, AlertTriangle, ChevronRight, MousePointerClick, Globe, BookOpenText, Star, Briefcase } from "lucide-react";
import MyList from "@/components/MyList";
import { computeMoria, gradesComplete, DEFAULT_COEF, SHORT } from "@/components/MoriaCalculator";
import { useFavorites } from "@/lib/useFavorites";
import { Logo } from "@/components/Logo";
import MoriaCalculator from "@/components/MoriaCalculator";
import { FIELDS, SCHOOLS_BY_FIELD, fmt } from "@/data/vaseisFields";
import { exportVaseisPdf } from "@/lib/vaseisPdf";

const BORDERLINE = 300;

function Diff({ b, p }) {
  if (p == null) return <span className="text-muted-foreground">—</span>;
  const d = b - p;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const cls = d > 0 ? "text-emerald-700 bg-emerald-50" : d < 0 ? "text-red-700 bg-red-50" : "text-muted-foreground bg-secondary";
  return (
    <span className={`inline-flex items-center gap-1 font-mono-accent text-xs px-2 py-0.5 rounded-sm ${cls}`}>
      <Icon className="h-3 w-3" />
      {d > 0 ? "+" : ""}{fmt(d)}
    </span>
  );
}

function ReachBadge({ base, moria }) {
  if (moria == null) return null;
  if (base <= moria) return <span data-testid="reach-yes" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm"><CheckCircle2 className="h-3 w-3" /> Πιάνεις</span>;
  if (base - moria <= BORDERLINE) return <span data-testid="reach-close" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 px-2 py-0.5 rounded-sm"><AlertTriangle className="h-3 w-3" /> Οριακά (−{fmt(base - moria)})</span>;
  return null;
}

function FieldCard({ field, active, count, top, onClick }) {
  const Icon = field.icon;
  return (
    <button
      data-testid={`vaseis-field-${field.id}`}
      onClick={onClick}
      className={`group text-left border rounded-sm p-5 transition-all ${active ? "bg-primary text-primary-foreground border-primary shadow-lg" : "bg-white border-border hover:border-primary"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className={`font-mono-accent text-[10px] uppercase tracking-[0.2em] ${active ? "text-white/70" : "text-accent"}`}>{field.short}</span>
        <Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: active ? "#fff" : field.color }} />
      </div>
      <div className="font-heading font-bold text-base leading-snug min-h-[2.75rem]">{field.name}</div>
      <div className={`mt-3 inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest ${active ? "text-white" : "text-primary group-hover:underline"}`}>
        {active ? <><CheckCircle2 className="h-3 w-3" /> Εμφανίζεται παρακάτω</> : <>Προβολή σχολών <ChevronRight className="h-3 w-3" /></>}
      </div>
      <div className={`mt-4 pt-3 border-t flex items-end justify-between gap-2 ${active ? "border-white/20" : "border-border"}`}>
        <div>
          <div className={`font-mono-accent text-[10px] uppercase tracking-widest ${active ? "text-white/60" : "text-muted-foreground"}`}>Σχολές</div>
          <div className="font-heading font-extrabold text-2xl leading-none mt-1">{count}</div>
        </div>
        <div className="text-right">
          <div className={`font-mono-accent text-[10px] uppercase tracking-widest ${active ? "text-white/60" : "text-muted-foreground"}`}>Υψηλότερη βάση</div>
          <div className="font-heading font-extrabold text-2xl leading-none mt-1">{fmt(top?.b)}</div>
        </div>
      </div>
    </button>
  );
}

function SchoolRow({ s, i, moria, fav, fieldId }) {
  const reached = moria != null && s.b <= moria;
  const coefs = s.k?.[fieldId];
  const starred = fav.has(s.c);
  return (
    <tr data-testid={`vaseis-row-${s.c}`} className={`border-b border-border/70 transition-colors ${reached ? "bg-emerald-50/60 hover:bg-emerald-50" : "hover:bg-secondary/50"}`}>
      <td className="py-3 pl-4 pr-2 font-mono-accent text-xs text-muted-foreground w-12 hidden sm:table-cell">{i + 1}</td>
      <td className="py-3 pl-3 sm:pl-1 pr-1 w-10 align-top">
        <button
          data-testid={`fav-toggle-${s.c}`}
          onClick={() => fav.toggle(s.c)}
          aria-label={starred ? "Αφαίρεση από τη λίστα" : "Προσθήκη στη λίστα"}
          aria-pressed={starred}
          className={`h-8 w-8 flex items-center justify-center rounded-sm transition-colors ${starred ? "text-amber-500" : "text-muted-foreground/40 hover:text-amber-500"}`}
        >
          <Star className={`h-5 w-5 ${starred ? "fill-amber-400" : ""}`} />
        </button>
      </td>
      <td className="py-3 pl-1 pr-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium leading-snug">{s.n}</span>
          <ReachBadge base={s.b} moria={moria} />
        </div>
        <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
          {s.i} · Κωδ. {s.c}
          {s.f.length > 1 && <span className="ml-2 text-accent">Πεδία {s.f.join("/")}</span>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
          {s.u && (
            <a data-testid={`school-site-${s.c}`} href={s.u} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-primary hover:underline">
              <Globe className="h-3 w-3" /> Ιστοσελίδα Τμήματος
            </a>
          )}
          {s.sep && (
            <a data-testid={`school-guide-${s.c}`} href={s.sep} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary hover:underline">
              <BookOpenText className="h-3 w-3" /> Οδηγός & Επαγγ. Δικαιώματα
            </a>
          )}
        </div>
        {s.j?.length > 0 && (
          <div data-testid={`jobs-${s.c}`} className="flex flex-wrap items-center gap-1.5 mt-2">
            <Briefcase className="h-3 w-3 text-muted-foreground" />
            {s.j.map((job) => (
              <span key={job} className="font-mono-accent text-[10px] tracking-wide bg-secondary text-foreground/80 px-2 py-0.5 rounded-sm">{job}</span>
            ))}
          </div>
        )}
        <div data-testid={`coefs-${s.c}`} className="font-mono-accent text-[10px] tracking-wider text-muted-foreground mt-1.5">
          Συντ.: {coefs
            ? SHORT[fieldId].map((l, j) => `${l} ${coefs[j]}%`).join(" · ")
            : <span className="italic">δεν υπάρχουν επίσημοι — ενδεικτικά 25% ανά μάθημα</span>}
        </div>
      </td>
      {moria != null && (
        <td data-testid={`my-moria-${s.c}`} className={`py-3 px-2 text-right font-heading font-bold text-base whitespace-nowrap hidden sm:table-cell ${reached ? "text-emerald-700" : "text-muted-foreground"}`}>{fmt(moria)}</td>
      )}
      <td className="py-3 px-2 text-right whitespace-nowrap">
        <div className="font-heading font-extrabold text-lg">{fmt(s.b)}</div>
        {moria != null && <div className={`sm:hidden font-mono-accent text-[10px] ${reached ? "text-emerald-700" : "text-muted-foreground"}`}>εσύ: {fmt(moria)}</div>}
      </td>
      <td className="py-3 px-2 text-right text-muted-foreground whitespace-nowrap hidden sm:table-cell">{fmt(s.p)}</td>
      <td className="py-3 px-2 text-right whitespace-nowrap hidden sm:table-cell"><Diff b={s.b} p={s.p} /></td>
      <td className="py-3 pl-2 pr-4 text-right text-muted-foreground hidden md:table-cell">{fmt(s.s)}</td>
    </tr>
  );
}

export default function Vaseis() {
  const [field, setField] = useState(2);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [grades, setGrades] = useState(["", "", "", ""]);
  const [onlyReach, setOnlyReach] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showList, setShowList] = useState(false);
  const fav = useFavorites();

  const current = FIELDS.find((f) => f.id === field);
  const all = SCHOOLS_BY_FIELD[field];
  const active = gradesComplete(grades);
  const moriaFor = useCallback((s, f = field) => (active && s.f.includes(f) ? computeMoria(grades, s.k?.[f] || DEFAULT_COEF) : null), [active, grades, field]);
  const reachedCount = useMemo(() => (active ? all.filter((s) => moriaFor(s) >= s.b).length : 0), [all, active, moriaFor]);
  const withCoefs = useMemo(() => all.filter((s) => s.k?.[field]).length, [all, field]);

  const rows = useMemo(() => {
    const norm = (t) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const needle = norm(q.trim());
    let arr = all.filter((s) => !needle || norm(`${s.n} ${s.i} ${s.c}`).includes(needle));
    if (onlyReach && active) arr = arr.filter((s) => moriaFor(s) >= s.b);
    if (sort === "asc") arr = [...arr].sort((a, b) => a.b - b.b);
    if (sort === "name") arr = [...arr].sort((a, b) => a.n.localeCompare(b.n, "el"));
    if (sort === "diff") arr = [...arr].sort((a, b) => (b.b - (b.p ?? b.b)) - (a.b - (a.p ?? a.b)));
    return arr;
  }, [all, q, sort, onlyReach, active, moriaFor]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportVaseisPdf({ field: current, rows, moriaFor: active ? moriaFor : null });
      toast.success("Το PDF δημιουργήθηκε.");
    } catch (e) {
      toast.error("Αποτυχία δημιουργίας PDF.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="vaseis-logo">
            <Logo className="h-10 w-12 transition-transform group-hover:rotate-[360deg] duration-700" ringColor="#0A1A4A" />
            <div className="leading-none">
              <div className="font-heading font-extrabold text-lg">Φυσική Επιλογή</div>
              <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Βάσεις 2026</div>
            </div>
          </Link>
          <Link to="/" data-testid="vaseis-back" className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-foreground/70 hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Αρχική
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10 max-w-3xl">
          <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-3">// Πανελλαδικές 2026</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
            <GraduationCap className="h-9 w-9 text-primary" strokeWidth={1.5} /> Βάσεις Σχολών ανά Πεδίο
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Τα μόρια του τελευταίου εισαγόμενου ανά Τμήμα για το 2026 (ΓΕΛ, Ημερήσια, Γενική Σειρά 90%) με σύγκριση προς το 2025.
            Πηγή: Υπουργείο Παιδείας, Θρησκευμάτων & Αθλητισμού, ανακοίνωση 23/07/2026.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-primary" data-testid="vaseis-field-hint">
          <MousePointerClick className="h-4 w-4" /> Επίλεξε Επιστημονικό Πεδίο για να δεις όλες τις σχολές του
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="vaseis-field-cards">
          {FIELDS.map((f) => (
            <FieldCard key={f.id} field={f} active={field === f.id} count={SCHOOLS_BY_FIELD[f.id].length} top={SCHOOLS_BY_FIELD[f.id][0]} onClick={() => { setField(f.id); setQ(""); setOnlyReach(false); document.getElementById("vaseis-results")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
          ))}
        </div>

        <motion.div id="vaseis-results" className="scroll-mt-24" key={field} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <div className="flex justify-end mb-4">
            <button
              data-testid="mylist-toggle"
              onClick={() => setShowList((v) => !v)}
              className={`inline-flex items-center gap-2 h-11 px-4 border rounded-sm font-mono-accent text-xs uppercase tracking-widest transition-colors ${showList ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}
            >
              <Star className={`h-4 w-4 ${fav.codes.length ? "text-amber-500 fill-amber-400" : ""}`} />
              Η λίστα μου
              <span data-testid="mylist-toggle-count" className={`font-heading font-extrabold text-sm px-2 py-0.5 rounded-sm ${showList ? "bg-white/20" : "bg-secondary"}`}>{fav.codes.length}</span>
            </button>
          </div>
          {showList && <MyList fav={fav} moriaFor={active ? moriaFor : null} onClose={() => setShowList(false)} />}
          <MoriaCalculator field={current} grades={grades} onGrades={setGrades} total={all.length} reached={reachedCount} withCoefs={withCoefs} />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="vaseis-field-title">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ background: current.color }} />
              {current.short} — {current.name}
              <span className="font-mono-accent text-xs text-muted-foreground font-normal ml-1" data-testid="vaseis-count">({rows.length})</span>
            </h2>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              {active && (
                <label data-testid="vaseis-only-reach" className="inline-flex items-center gap-2 h-11 px-3 border border-border rounded-sm bg-white text-xs font-mono-accent uppercase tracking-widest cursor-pointer select-none">
                  <input type="checkbox" checked={onlyReach} onChange={(e) => setOnlyReach(e.target.checked)} className="accent-primary h-4 w-4" />
                  Μόνο όσες πιάνω
                </label>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="vaseis-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Αναζήτηση σχολής, ιδρύματος, κωδικού…"
                  className="h-11 w-full sm:w-72 pl-10 pr-3 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <select
                  data-testid="vaseis-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-11 pl-10 pr-8 border border-border rounded-sm bg-white text-sm font-mono-accent focus:outline-none focus:border-primary appearance-none"
                >
                  <option value="desc">Βάση ↓</option>
                  <option value="asc">Βάση ↑</option>
                  <option value="diff">Μεγαλύτερη άνοδος</option>
                  <option value="name">Αλφαβητικά</option>
                </select>
              </div>
              <button
                data-testid="vaseis-export-pdf"
                onClick={handleExport}
                disabled={exporting || rows.length === 0}
                className="inline-flex items-center justify-center gap-2 h-11 px-4 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent transition-colors disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                PDF
              </button>
            </div>
          </div>

          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vaseis-table">
                <thead className="bg-secondary/60 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="py-3 pl-4 pr-2 text-left hidden sm:table-cell">#</th>
                    <th className="py-3 pl-3 pr-1 text-left"><Star className="h-3.5 w-3.5" /></th>
                    <th className="py-3 px-2 text-left">Σχολή / Τμήμα</th>
                    {active && <th className="py-3 px-2 text-right hidden sm:table-cell">Μόριά σου</th>}
                    <th className="py-3 px-2 text-right">Βάση 2026</th>
                    <th className="py-3 px-2 text-right hidden sm:table-cell">Βάση 2025</th>
                    <th className="py-3 px-2 text-right hidden sm:table-cell">Διαφορά</th>
                    <th className="py-3 pl-2 pr-4 text-right hidden md:table-cell">Θέσεις</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s, i) => <SchoolRow key={s.c} s={s} i={i} moria={moriaFor(s)} fav={fav} fieldId={field} />)}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-12 text-center text-muted-foreground" data-testid="vaseis-empty">Δεν βρέθηκαν σχολές{q ? ` για «${q}»` : ""}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
            * Οι βάσεις αφορούν την κατηγορία ΓΕΛ Ημερήσια — Γενική Σειρά (90%). Τμήματα κοινά σε περισσότερα πεδία εμφανίζονται σε κάθε πεδίο. Οι θέσεις αναφέρονται στις θέσεις της κατηγορίας κατόπιν μεταφοράς. Τα μόρια ανά σχολή υπολογίζονται με τους επίσημους συντελεστές βαρύτητας 2026 (ΦΕΚ 7145/Β/2025) — όπου δεν υπάρχουν (π.χ. Μουσικών/Θεατρικών Σπουδών με ειδικά μαθήματα) χρησιμοποιούνται ενδεικτικά 25% ανά μάθημα. Το link «Ιστοσελίδα Τμήματος» οδηγεί στον επίσημο ιστότοπο του Τμήματος (πρόγραμμα σπουδών, επαγγελματικά δικαιώματα) και το «Οδηγός & Επαγγ. Δικαιώματα» στη σελίδα του Τμήματος στο sep4u.gr.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
